import { Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type {
  GridAggregationFunction,
  GridAggregationModel,
  GridApiPremium,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnOrderChangeParams,
  GridDataSource,
  GridSkeletonCellProps,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import {
  GridSkeletonCell,
  gridColumnLookupSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid.tsx";
import { getBiDataGridStyles } from "../../../../utilities/Utilities.tsx";
import { stopEventPropagation } from "../../../../utilities/stopEventPropagation.ts";

import useMergedSx from "@entrilia/shared/hooks/useMergedSx.ts";
import type { DrillDownRow } from "../../../../../../../../api/types/svTypes.ts";
import GridLoadingOverlay from "../../../common/GridLoadingOverlay.tsx";
import { HIGHLIGHTED_COLUMN_CLASS, PAGE_SIZE } from "./utils/drillDownHelper.tsx";

interface Props {
  columns: GridColDef[];
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  sortModel: GridSortModel;
  selectableRows: boolean;
  aggregationModel: GridAggregationModel;
  onSortingChanged: (model: GridSortModel, details: GridCallbackDetails) => void;
  onCellClicked: (params: GridCellParams<DrillDownRow>, event: React.MouseEvent<HTMLElement>) => void;
  onColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
}

const gridSx: SxProps<Theme> = (theme) => {
  const highlightBorder = `1px solid ${alpha(theme.palette.primary.main, 0.5)}`;
  return {
    '& [data-id="auto-generated-group-footer-root"] .MuiDataGrid-cell': {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    [`.MuiDataGrid-columnHeaders [role=row] .MuiDataGrid-columnHeader.${HIGHLIGHTED_COLUMN_CLASS}`]: {
      backgroundColor: alpha(theme.palette.primary.light, 0.25),
      borderTop: highlightBorder,
      borderLeft: highlightBorder,
      borderRight: highlightBorder,
      borderTopLeftRadius: 2,
      borderTopRightRadius: 2,
      "& .MuiDataGrid-sortButton": {
        backgroundColor: "transparent",
      },
    },
    [`.MuiDataGrid-cell.${HIGHLIGHTED_COLUMN_CLASS}`]: {
      backgroundColor: alpha(theme.palette.primary.light, 0.15),
      borderLeft: highlightBorder,
      borderRight: highlightBorder,
    },
    [`[role=row]:last-of-type .MuiDataGrid-cell.${HIGHLIGHTED_COLUMN_CLASS}`]: {
      borderBottom: highlightBorder,
      borderBottomLeftRadius: 2,
      borderBottomRightRadius: 2,
    },
  };
};

const custom: GridAggregationFunction<number, number> = {
  label: "",
  apply: () => 0,
  columnTypes: ["string"],
};
const aggregationFunctions: Record<string, GridAggregationFunction<number, number>> = {
  custom: custom,
};

const HighlightAwareSkeletonCell = (props: GridSkeletonCellProps) => {
  const apiRef = useGridApiContext();
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const isHighlighted = !!props.field && columnLookup[props.field]?.cellClassName === HIGHLIGHTED_COLUMN_CLASS;
  return <GridSkeletonCell {...props} className={isHighlighted ? HIGHLIGHTED_COLUMN_CLASS : undefined} />;
};

const gridSlots = { skeletonCell: HighlightAwareSkeletonCell, loadingOverlay: GridLoadingOverlay };

const DrillDownGrid = ({
  columns,
  apiRef,
  dataSource,
  aggregationModel,
  onSortingChanged,
  sortModel,
  onCellClicked,
  selectableRows,
  onColumnOrderChange,
}: Props) => {
  const sx = useMergedSx(getBiDataGridStyles, gridSx);

  return (
    <Stack onDrop={stopEventPropagation} sx={{ position: "relative", flex: 1 }}>
      <DataGrid<DrillDownRow>
        lazyLoading
        sortingMode="server"
        paginationModel={{ page: 0, pageSize: PAGE_SIZE }}
        dataSource={dataSource}
        apiRef={apiRef}
        columns={columns}
        sortModel={sortModel}
        onColumnOrderChange={onColumnOrderChange}
        onSortModelChange={onSortingChanged}
        onCellClick={onCellClicked}
        showColumnVerticalBorder
        rowHeight={32}
        columnHeaderHeight={32}
        selectableRows={selectableRows}
        sx={sx}
        sortingOrder={["asc", "desc"]}
        aggregationModel={aggregationModel}
        aggregationFunctions={aggregationFunctions}
        slots={gridSlots}
      />
    </Stack>
  );
};

export default DrillDownGrid;
